/**
 * Web Serial delivery of ESC/POS payloads to a thermal printer.
 *
 * The backend renders a token to raw ESC/POS bytes; when it cannot reach the
 * printer itself (the default in a cloud deployment) it returns those bytes
 * for the workstation browser to deliver. This module is that delivery path:
 * a Chromium browser opens the USB/serial thermal printer the operator has
 * granted access to and streams the bytes — no OS print dialog, no scaling.
 *
 * `navigator.serial` is Chromium-only and needs a secure context (HTTPS or
 * localhost); callers must handle `PrintError` with code `unsupported`.
 */

// --- Minimal Web Serial typings (absent from lib.dom.d.ts) --------------
interface SerialPort {
  readonly writable: WritableStream<Uint8Array> | null;
  open(options: { baudRate: number }): Promise<void>;
}
interface Serial {
  getPorts(): Promise<SerialPort[]>;
  requestPort(options?: { filters?: unknown[] }): Promise<SerialPort>;
}
declare global {
  interface Navigator {
    readonly serial?: Serial;
  }
}

/** Baud rate for the serial link. 9600 is the ESC/POS default; raise it
 *  (19200 / 38400 / 115200) here if a faster printer mis-prints. */
const PRINTER_BAUD_RATE = 9600;

export type PrintErrorCode =
  | 'unsupported'
  | 'no-printer'
  | 'cancelled'
  | 'device';

/** A delivery failure with a stable `code` the UI can map to a message. */
export class PrintError extends Error {
  constructor(
    public readonly code: PrintErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'PrintError';
  }
}

/** True when this browser can talk to a serial printer at all. */
export function isWebSerialSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    Boolean(navigator.serial) &&
    window.isSecureContext
  );
}

// The granted port is reused across prints so the operator authorises the
// printer once per session, not once per token.
let cachedPort: SerialPort | null = null;

function decodePayload(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function resolvePort(allowPrompt: boolean): Promise<SerialPort> {
  const serial = navigator.serial;
  if (!serial) throw new PrintError('unsupported');
  if (cachedPort) return cachedPort;

  // A port the operator granted earlier on this device — no prompt needed.
  const [granted] = await serial.getPorts();
  if (granted) {
    cachedPort = granted;
    return granted;
  }
  // None granted yet: only a user gesture may raise the picker.
  if (!allowPrompt) throw new PrintError('no-printer');
  try {
    cachedPort = await serial.requestPort();
    return cachedPort;
  } catch {
    // The picker was dismissed, or no device was chosen.
    throw new PrintError('cancelled');
  }
}

/**
 * Stream a base64 ESC/POS payload to the thermal printer.
 *
 * @param base64 raw ESC/POS bytes, base64-encoded (the backend's
 *        `payload_base64`).
 * @param allowPrompt whether a missing printer may raise the browser's port
 *        picker — pass `true` only from a user gesture (a click).
 */
export async function printEscPos(
  base64: string,
  { allowPrompt }: { allowPrompt: boolean },
): Promise<void> {
  if (!isWebSerialSupported()) throw new PrintError('unsupported');
  const port = await resolvePort(allowPrompt);
  try {
    if (!port.writable) {
      await port.open({ baudRate: PRINTER_BAUD_RATE });
    }
    const writer = port.writable!.getWriter();
    try {
      await writer.write(decodePayload(base64));
    } finally {
      writer.releaseLock();
    }
  } catch (error) {
    if (error instanceof PrintError) throw error;
    // The cached port likely went stale (printer unplugged) — drop it so the
    // next attempt re-resolves instead of failing forever.
    cachedPort = null;
    throw new PrintError(
      'device',
      error instanceof Error ? error.message : String(error),
    );
  }
}
